export function normalizeViewedIds(value, excludeId = 0, limit = 8) {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.map(Number).filter(id => Number.isSafeInteger(id) && id > 0))]
    .filter(id => id !== Number(excludeId)).slice(0, limit);
}

export function selectDiscoveryProducts(products, excludeId, excludedHandles, limit = 8) {
  const seen = new Set();
  return (Array.isArray(products) ? products : []).filter(product => {
    const id = Number(product?.id);
    if (!Number.isSafeInteger(id) || id <= 0 || id === Number(excludeId)
      || typeof product.handle !== 'string' || !product.handle
      || excludedHandles.has(product.handle) || seen.has(id)) return false;
    seen.add(id);
    return true;
  }).slice(0, limit);
}

function isLocalBrowseUrl(url) {
  return typeof url === 'string' && url.startsWith('/') && !url.startsWith('//')
    && !/[\s\\]/.test(url);
}

export function getCarouselBrowseTarget({ mode, discovery = {}, renderedIds = [], excludeId, excludedHandles = new Set(), allProductsUrl }) {
  if (!renderedIds.length) return null;
  if (mode === 'history') {
    return isLocalBrowseUrl(allProductsUrl) ? { url: allProductsUrl, label: 'Continue shopping' } : null;
  }
  if (mode !== 'discovery' || !isLocalBrowseUrl(discovery.url)
    || typeof discovery.title !== 'string' || !discovery.title.trim()) return null;
  const shown = new Set(renderedIds.map(Number));
  const hasMore = selectDiscoveryProducts(discovery.products, excludeId, excludedHandles, 50)
    .some(product => !shown.has(Number(product.id)));
  return hasMore ? { url: discovery.url, label: `Shop all ${discovery.title.trim()}` } : null;
}

/******/ (() => { // webpackBootstrap
if (!customElements.get('recently-viewed-products')) {
  class RecentlyViewedProducts extends HTMLElement {
    constructor() {
      super();

      this.abortController = null; // Abort controller for request cancellation
      this.initialImagesPromoted = false;
      this.discoveryObserver = null;
      this.mode = null;
      this.disconnected = false;
      this.cacheDOMElements();
      this.bindEventHandlers();
    }

    connectedCallback() {
      this.disconnected = false;
      this.addEventListeners();
      this.debouncedRenderRecentlyViewedProducts();
    }

    cacheDOMElements() {
      this.sectionId = this.getAttribute('data-wetheme-section-id');
      this.isPreviewMode = this.getAttribute('data-is-preview-mode') == 'true';
      this.displayMode = this.getAttribute('data-display-mode') === 'category' ? 'category' : 'history';
      this.wrapper = this.closest('[data-recently-viewed-wrapper]');
      try {
        this.discovery = JSON.parse(this.querySelector('[data-discovery-config]')?.textContent || '{}');
      } catch {
        this.discovery = {};
      }
      if (!this.sectionId || !this.wrapper) return;
    }

    bindEventHandlers() {
      this.debouncedRenderRecentlyViewedProductsBound = this.debouncedRenderRecentlyViewedProducts.bind(this);
      this.recommendationsLoadedBound = this.removeRecommendationDuplicates.bind(this);
    }

    addEventListeners() {
      document.addEventListener('recommendations:loaded', this.recommendationsLoadedBound);
      if (window.Shopify.designMode) {
        document.addEventListener('shopify:section:load', this.debouncedRenderRecentlyViewedProductsBound);
      }
    }

    debouncedRenderRecentlyViewedProducts() {
      if (this.renderTimeout) {
        clearTimeout(this.renderTimeout);
      }

      // Cancel any pending requests
      if (this.abortController) {
        this.abortController.abort();
      }

      // Use longer delay in design mode to allow server to process setting changes
      const delay = window.Shopify.designMode ? 800 : 0;
      this.renderTimeout = setTimeout(() => this.renderRecentlyViewedProducts(), delay);
    }

    async renderRecentlyViewedProducts() {
      if (!this.wrapper || !this.sectionId) return;
      // Category discovery is independent of a visitor's browsing history.
      // The separate history instance never substitutes category products.
      if (this.displayMode === 'category') {
        this.scheduleDiscovery();
        return;
      }
      let storedProducts = [];
      try {
        storedProducts = JSON.parse(localStorage.getItem('theme:recently-viewed-products') || '[]');
      } catch {
        // Missing, blocked or malformed history leaves only this section hidden.
      }
      const ids = normalizeViewedIds(storedProducts, this.getAttribute('exclude-id'), this.productLimit);
      if (!ids.length && !this.isPreviewMode) {
        this.hideEmptySection();
        return;
      }
      await this.loadProducts(ids, 'history');
    }

    get productLimit() {
      return Math.max(1, Math.min(50, Number(this.dataset.productsToShow) || 8));
    }

    recommendationHandles(container = document) {
      return new Set([...container.querySelectorAll('product-recommendations [data-product-card-link]')]
        .map(link => link.getAttribute('href')?.split('?')[0].match(/\/products\/([^/]+)\/?$/)?.[1])
        .filter(Boolean));
    }

    async getRecommendationHandles() {
      const element = document.querySelector('product-recommendations[data-wetheme-section-type="template--product--recommendations"]');
      const excluded = this.recommendationHandles();
      const earlyRequest = window.mdhRecommendationRequests?.get(element?.dataset.recommendationsUrl);
      if (earlyRequest && element?.getAttribute('aria-busy') === 'true') {
        // Reuse the existing request: no additional recommendations API call.
        let timeout;
        try {
          const text = await Promise.race([
            earlyRequest,
            new Promise(resolve => { timeout = setTimeout(() => resolve(null), 5000); }),
          ]);
          if (text) {
            const container = document.createElement('div');
            container.innerHTML = text;
            this.recommendationHandles(container).forEach(handle => excluded.add(handle));
          }
        } catch {} finally { clearTimeout(timeout); }
      }
      this.recommendationHandles().forEach(handle => excluded.add(handle));
      return excluded;
    }

    scheduleDiscovery() {
      if (this.disconnected || this.discoveryObserver || this.mode === 'discovery-loading') return;
      if (!this.discovery.products?.length) {
        this.hideEmptySection();
        return;
      }
      this.mode = 'discovery-pending';
      this.dataset.mode = this.mode;
      this.wrapper.style.display = 'none';
      this.setAttribute('aria-busy', 'false');
      const start = () => {
        this.discoveryObserver?.disconnect();
        this.discoveryObserver = null;
        this.loadDiscovery();
      };
      if (window.Shopify.designMode || !('IntersectionObserver' in window)) {
        start();
      } else {
        this.discoveryObserver = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) start();
        }, { rootMargin: '400px 0px' });
        // The inner wrapper is hidden; its Shopify section still has a position.
        this.discoveryObserver.observe(this.closest('.shopify-section') || this.wrapper.parentElement);
      }
    }

    async loadDiscovery() {
      if (this.disconnected || this.mode === 'discovery-loading') return;
      this.mode = 'discovery-loading';
      this.dataset.mode = this.mode;
      const excluded = await this.getRecommendationHandles();
      if (this.disconnected) return;
      const candidates = selectDiscoveryProducts(this.discovery.products,
        this.getAttribute('exclude-id'), excluded, Math.min(8, this.productLimit));
      if (!candidates.length) {
        this.hideEmptySection();
        return;
      }
      await this.loadProducts(candidates.map(product => product.id), 'discovery');
    }

    async loadProducts(ids, mode) {
      this.abortController?.abort();
      const controller = new AbortController();
      this.abortController = controller;
      const timeout = setTimeout(() => controller.abort(), 8000);
      this.setAttribute('aria-busy', 'true');
      try {
        const queryString = ids.map(id => `id:${id}`).join(' OR ');
        const root = window.Shopify.routes.root || '/';
        let url = `${root}search?type=product&q=${queryString}&section_id=${this.sectionId}`;
        if (window.Shopify.designMode) url += `&t=${Date.now()}`;
        const earlyRequest = mode === 'history' && !window.Shopify.designMode
          ? window.mdhRecentlyViewedRequests?.get(this.sectionId) : null;
        const requestMarkup = async () => {
          const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error(`Unable to load product suggestions (${response.status})`);
          return response.text();
        };
        const markup = earlyRequest?.url === url
          ? earlyRequest.request.then(text => text || requestMarkup()) : requestMarkup();
        let abortHandler;
        let responseText;
        try {
          responseText = await Promise.race([markup, new Promise((resolve, reject) => {
            abortHandler = () => reject(new Error('Product suggestions request aborted'));
            controller.signal.addEventListener('abort', abortHandler, { once: true });
            if (controller.signal.aborted) abortHandler();
          })]);
        } finally {
          controller.signal.removeEventListener('abort', abortHandler);
        }
        const excluded = mode === 'discovery' ? await this.getRecommendationHandles() : new Set();
        if (this.disconnected || controller !== this.abortController) return;
        if (controller.signal.aborted) {
          this.hideEmptySection();
          return;
        }
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = responseText;
        const recentlyViewedProductsElement = tempDiv.querySelector('recently-viewed-products');
        const cards = recentlyViewedProductsElement?.querySelectorAll('[data-history-product-id]') || [];
        cards.forEach(card => {
          if (excluded.has(card.dataset.historyProductHandle)
            || card.dataset.historyProductId === this.getAttribute('exclude-id')) card.remove();
        });
        if (recentlyViewedProductsElement?.querySelector('[data-history-product-id]') || this.isPreviewMode) {
          // Avoid the theme's staggered reveal on dynamically inserted cards;
          // it can otherwise make cards appear to float over the section while
          // the slider and images initialize.
          recentlyViewedProductsElement.querySelectorAll('[data-animate]').forEach((element) => {
            element.removeAttribute('data-animate');
            element.removeAttribute('data-animate-left');
            element.removeAttribute('data-animate-left-bold');
            element.removeAttribute('data-animate-right-bold');
            element.removeAttribute('data-animate-order');
            element.removeAttribute('data-animate-order-ipad');
            element.removeAttribute('data-animate-order-mobile');
            element.removeAttribute('data-animate-overflow-item');
          });
          recentlyViewedProductsElement.querySelectorAll('[data-animate-overflow-container]').forEach((element) => {
            element.removeAttribute('data-animate-overflow-container');
          });

          this.decorateRecentlyViewedImages(recentlyViewedProductsElement, mode);

          // Replace the children of this element with the imported nodes from the parsed HTML
          this.replaceChildren(...recentlyViewedProductsElement.childNodes);
          this.mode = mode;
          this.dataset.mode = mode;
          if (mode === 'discovery') this.decorateDiscoverySection();
          this.syncBrowseEndCard();
          this.wrapper.style.removeProperty('display');
          this.setAttribute('aria-busy', 'false');
          this.bindRecentlyViewedImageStates();

          // Re-initialize quick add, quick view, etc
          window.eventBus?.emit('recentlyViewed:updated', { sectionId: this.sectionId });
        } else {
          this.hideEmptySection();
        }
      } catch (e) {
        if (this.disconnected || controller !== this.abortController) return;
        this.hideEmptySection();
      } finally {
        clearTimeout(timeout);
      }
    }

    decorateDiscoverySection() {
      const heading = this.querySelector('.section-title');
      if (heading) heading.textContent = `More from ${this.discovery.title}`;
      this.querySelector('[data-grid-slider-wrapper]')?.setAttribute('aria-label', `More from ${this.discovery.title}`);
      this.querySelector('.section-description')?.remove();
      const url = this.discovery.url;
      if (typeof url !== 'string' || !url.startsWith('/') || url.startsWith('//')) return;
      const browse = document.createElement('p');
      browse.className = 'recently-viewed__browse';
      const link = document.createElement('a');
      link.href = url;
      link.textContent = `Browse all ${this.discovery.title} →`;
      browse.append(link);
      this.querySelector('[data-grid-slider-wrapper]')?.append(browse);
    }

    syncBrowseEndCard() {
      const slider = this.querySelector('grid-slider');
      const slides = slider?.querySelector('.swiper-wrapper');
      const template = this.querySelector('[data-carousel-browse-template]');
      if (!slides || !template) return;
      const existing = slides.querySelector('[data-carousel-browse-card]');
      const target = getCarouselBrowseTarget({
        mode: this.mode, discovery: this.discovery,
        renderedIds: [...slides.querySelectorAll('[data-history-product-id]')].map(card => card.dataset.historyProductId),
        excludeId: this.getAttribute('exclude-id'), excludedHandles: this.recommendationHandles(),
        allProductsUrl: template.content.querySelector('[data-carousel-browse-link]')?.getAttribute('href'),
      });
      if (!target) existing?.remove();
      else {
        const card = existing || template.content.firstElementChild.cloneNode(true);
        card.querySelector('[data-carousel-browse-link]').setAttribute('href', target.url);
        card.querySelector('[data-carousel-browse-label]').textContent = target.label;
        if (!existing) slides.append(card);
      }
      slider.dataset.productLimit = this.productLimit + (target ? 1 : 0);
      slider.swiperInstance?.update();
    }

    removeRecommendationDuplicates() {
      // History is a record of the visitor's own choices, even when a viewed
      // product also happens to appear in You may also like.
      if (this.mode !== 'discovery') return;
      const excluded = this.recommendationHandles();
      let removed = false;
      this.querySelectorAll('[data-history-product-id]').forEach(card => {
        if (excluded.has(card.dataset.historyProductHandle)) { card.remove(); removed = true; }
      });
      if (!removed) return;
      if (!this.querySelector('[data-history-product-id]')) {
        this.hideEmptySection();
      } else {
        this.syncBrowseEndCard();
        this.querySelector('grid-slider')?.swiperInstance?.update();
      }
    }

    hideEmptySection() {
      this.wrapper.style.display = 'none';
      this.mode = 'empty';
      this.dataset.mode = 'empty';
      this.setAttribute('aria-busy', 'false');
    }

    decorateRecentlyViewedImages(container, mode = 'history') {
      const initialImageCount = this.getInitialImageCount();

      container.querySelectorAll('img[data-product-card-image]').forEach((image, index) => {
        image.decoding = 'async';
        image.classList.add('recently-viewed__product-image');

        const wrapper = image.closest('.responsive-image-wrapper');
        wrapper?.classList.add('recently-viewed__image-loading');

        // Some PNG sources can omit the Liquid-generated placeholder URL.
        // Derive the same tiny Shopify CDN transform from the card image URL.
        if (wrapper?.hasAttribute('data-low-quality-placeholder')
          && !wrapper.style.getPropertyValue('--responsive-image-placeholder')) {
          const imageUrl = image.getAttribute('src');
          if (imageUrl) {
            const placeholderUrl = new URL(imageUrl, window.location.origin);
            placeholderUrl.searchParams.set('width', '120');
            placeholderUrl.searchParams.set('quality', '45');
            wrapper.style.setProperty('--responsive-image-placeholder', `url('${placeholderUrl}')`);
          }
        }

        // Apply final priority before insertion so the browser discovers only
        // the initially visible cards as high-priority work.
        if (index < initialImageCount) {
          image.loading = 'eager';
          image.fetchPriority = mode === 'discovery' ? 'auto' : 'high';
        }
      });

      this.initialImagesPromoted = true;
    }

    bindRecentlyViewedImageStates() {
      this.querySelectorAll('img[data-product-card-image]').forEach((image) => {
        const wrapper = image.closest('.responsive-image-wrapper');
        if (!wrapper) return;

        const revealImage = () => {
          if (wrapper.classList.contains('recently-viewed__image-loaded')) return;
          wrapper.classList.remove('recently-viewed__image-loading');
          wrapper.classList.add('recently-viewed__image-loaded');
        };

        const revealAfterDecode = () => {
          if (typeof image.decode === 'function') {
            image.decode().catch(() => {}).finally(revealImage);
          } else {
            revealImage();
          }
        };

        if (image.complete && image.naturalWidth > 0) {
          revealAfterDecode();
        } else {
          image.addEventListener('load', revealAfterDecode, { once: true });
          image.addEventListener('error', () => {
            wrapper.classList.remove('recently-viewed__image-loading');
            wrapper.classList.add('recently-viewed__image-error');
          }, { once: true });
        }
      });
    }

    getInitialImageCount() {
      if (window.matchMedia('(max-width: 809px)').matches) return 2;
      if (window.matchMedia('(max-width: 1024px)').matches) return 4;
      return 5;
    }

    disconnectedCallback() {
      this.disconnected = true;
      this.discoveryObserver?.disconnect();
      this.discoveryObserver = null;
      document.removeEventListener('recommendations:loaded', this.recommendationsLoadedBound);
      if (this.renderTimeout) {
        clearTimeout(this.renderTimeout);
      }

      // Cancel any pending requests
      if (this.abortController) {
        this.abortController.abort();
      }

      if (window.Shopify.designMode) {
        document.removeEventListener('shopify:section:load', this.debouncedRenderRecentlyViewedProductsBound);
      }
    }
  }

  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}

/******/ })()
;
